import { EntryCollection } from "@/types/entryCollection";
import { storage } from "@/lib/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { CaptureMetadata } from "@/types/annotations";
import { DocumentReference, Timestamp } from "firebase/firestore";
import type { EntryConfigType } from "@/types/entryConfig";
import type { AnalysisReviewStatus, AnalysisStatus } from "@/types/entry";
import { formatMetricValue } from "@/types/measurementMetrics";
import { getAnnotationMetadataCacheKey, getDownloadUrlCacheKey } from "./cacheKeyUtils";
import { formatRoundedCountPerSquareMeterPerAcre } from "@/lib/standCountUtils";
import {
  formatTimerDuration,
  getMostRecentStoppedTimerMs,
  getMostRecentStoppedTimerMsForSample,
  getStoppedTimerMs,
  timerMsToSeconds,
} from "./timerValueUtils";

export {
  formatTimerDuration,
  getMostRecentStoppedTimerMs,
  getMostRecentStoppedTimerMsForSample,
  getStoppedTimerMs,
  timerMsToSeconds,
} from "./timerValueUtils";

/**
 * Entry types that support annotation rendering (masks, bounding boxes, measurements)
 */
export const MEASUREMENT_TYPES: EntryConfigType[] = [
  "imageMeasurement",
  "standCountMeasurement",
  "leafAreaIndex",
  "ruler",
  "rowPlantedStandCount",
];

/**
 * Check if an entry type supports annotation overlay rendering
 */
export function isMeasurementType(type: EntryConfigType | string | undefined): boolean {
  if (!type) return false;
  return MEASUREMENT_TYPES.includes(type as EntryConfigType);
}

/**
 * Represents an image with its metadata
 */
export interface EntryImage {
  dataCollectionOnly?: boolean;
  reviewPriority?: number;
  url: string;
  type:
    | "photo"
    | "leafAreaIndex"
    | "imageMeasurement"
    | "standCountMeasurement"
    | "rowPlantedStandCount"
    | "ruler"
    | "annotated";
  sampleKey: string;
  /** Parent sample label for collection captures; sampleKey still identifies the individual image. */
  displaySampleKey?: string;
  uploadStatus?: string;
  /** Path to annotation metadata JSON (if image has annotations) */
  captureMetadataCloudPath?: string;
  /** Full path to the depth PNG when the entry has one */
  depthCloudPath?: string;
  /** ID of the entry collection this image belongs to */
  collectionId?: string;
  /** Name of the trait/entry config this image belongs to */
  traitName?: string;
  /** Timestamp for cache invalidation on re-uploads */
  updatedAt?: Timestamp;
  /** OCR-extracted text from the image */
  extractedImageText?: string;
  /** Whether the user manually edited the extracted text */
  extractedImageTextUserDidEdit?: boolean;
  analysisStatus?: AnalysisStatus;
  analysisReviewStatus?: AnalysisReviewStatus;
}

export interface TranscriptionSample {
  plotId: string;
  sampleKey: string;
  text: string;
  cloudAudioPath?: string;
  timestamp?: Timestamp;
  createdByRef?: DocumentReference;
  createdById?: string;
}

/**
 * In-memory cache for Firebase Storage download URLs.
 * Keys include uploadStatus when provided to invalidate on re-uploads.
 */
const downloadUrlCache = new Map<string, string>();

/**
 * In-memory cache for annotation metadata JSON.
 * Only versioned lookups are cached. Unversioned callers keep their historical
 * fresh-fetch behavior so exports and query hooks do not read stale viewer data.
 * Null results are evicted so transient Storage errors can retry later.
 */
const annotationMetadataCache = new Map<string, Promise<CaptureMetadata | null>>();
const MAX_ANNOTATION_METADATA_CACHE_SIZE = 100;

function setAnnotationMetadataCache(cacheKey: string, promise: Promise<CaptureMetadata | null>): void {
  annotationMetadataCache.set(cacheKey, promise);

  while (annotationMetadataCache.size > MAX_ANNOTATION_METADATA_CACHE_SIZE) {
    const oldestKey = annotationMetadataCache.keys().next().value;
    if (!oldestKey) break;
    annotationMetadataCache.delete(oldestKey);
  }
}

function touchAnnotationMetadataCache(cacheKey: string, promise: Promise<CaptureMetadata | null>): void {
  annotationMetadataCache.delete(cacheKey);
  setAnnotationMetadataCache(cacheKey, promise);
}

export function clearAnnotationMetadataCache(): void {
  annotationMetadataCache.clear();
}

export function invalidateAnnotationMetadataCache(captureMetadataCloudPath?: string): void {
  if (!captureMetadataCloudPath) {
    clearAnnotationMetadataCache();
    return;
  }

  for (const key of Array.from(annotationMetadataCache.keys())) {
    if (key === captureMetadataCloudPath || key.startsWith(`${captureMetadataCloudPath}|`)) {
      annotationMetadataCache.delete(key);
    }
  }
}

export function buildEmulatorStorageDownloadUrl(storagePath: string, bucket: string, port: string | number): string {
  return `http://localhost:${port}/download/storage/v1/b/${bucket}/o/${encodeURIComponent(storagePath)}?alt=media`;
}

function shouldUseEmulatorStorageMediaUrl(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_USE_PRODUCTION !== "true";
}

export async function getStorageDownloadURL(storagePath: string): Promise<string> {
  if (shouldUseEmulatorStorageMediaUrl()) {
    const bucket = storage.app.options.storageBucket ?? "glean-ag.firebasestorage.app";
    const port = import.meta.env.VITE_STORAGE_PORT || "9199";
    return buildEmulatorStorageDownloadUrl(storagePath, bucket, port);
  }

  const storageRef = ref(storage, storagePath);
  return getDownloadURL(storageRef);
}

/**
 * Convert Firebase Storage path to download URL.
 * Uses an in-memory cache to avoid redundant Firebase API calls.
 *
 * @param storagePath - Firebase Storage path to the file
 * @param uploadStatus - Optional upload status (not used for cache invalidation)
 * @param updatedAt - Optional timestamp to include in cache key.
 *   When an entry is updated (e.g., re-upload), updatedAt changes and cache key changes, forcing re-fetch.
 */
export async function getImageDownloadURL(
  storagePath: string,
  uploadStatus?: string,
  updatedAt?: Timestamp
): Promise<string> {
  const cacheKey = getDownloadUrlCacheKey(storagePath, updatedAt);

  const cached = downloadUrlCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const url = await getStorageDownloadURL(storagePath);
    downloadUrlCache.set(cacheKey, url);
    return url;
  } catch (error) {
    console.error("Error getting download URL for path:", storagePath, error);
    return "";
  }
}

/**
 * Fetches and parses annotation metadata JSON from Firebase Storage.
 *
 * Returns null on any error (missing file, parse error, etc.) for graceful degradation.
 * Many entries won't have metadata, which is expected and not an error condition.
 * Versioned reads with updatedAt are cached and deduped; unversioned reads fetch fresh.
 * Null versioned results are evicted so transient Storage failures can retry later.
 *
 * @param captureMetadataCloudPath - Full Firebase Storage path to metadata.json file from entry
 * @param updatedAt - Optional entry/media timestamp used as the versioned cache key
 * @returns Parsed CaptureMetadata object, or null if unavailable/invalid
 *
 * @example
 * // From an entry with captureMetadataCloudPath
 * const metadata = await getAnnotationMetadata(entry.captureMetadataCloudPath);
 * if (metadata?.annotations) {
 *   console.log(`Found ${metadata.annotations.annotations.length} annotations`);
 * }
 */
export async function getAnnotationMetadata(
  captureMetadataCloudPath?: string,
  updatedAt?: Timestamp
): Promise<CaptureMetadata | null> {
  if (!captureMetadataCloudPath) {
    return null;
  }

  const cacheKey = updatedAt ? getAnnotationMetadataCacheKey(captureMetadataCloudPath, updatedAt) : null;
  if (cacheKey) {
    const cached = annotationMetadataCache.get(cacheKey);
    if (cached) {
      touchAnnotationMetadataCache(cacheKey, cached);
      return cached;
    }
  }

  const fetchPromise = (async (): Promise<CaptureMetadata | null> => {
    try {
      const downloadURL = await getStorageDownloadURL(captureMetadataCloudPath);

      // Fetch and parse JSON
      const response = await fetch(downloadURL);
      if (!response.ok) {
        console.warn(`Failed to fetch metadata: ${response.status} ${response.statusText}`);
        return null;
      }

      const metadata: CaptureMetadata = await response.json();
      return metadata;
    } catch (error) {
      // Expected errors: file not found, parse errors, network issues
      // Many entries won't have metadata - this is normal
      if (error instanceof Error && !error.message.includes("does not exist")) {
        console.warn(`Error fetching annotation metadata:`, error.message);
      }
      return null;
    }
  })();

  if (cacheKey) {
    setAnnotationMetadataCache(cacheKey, fetchPromise);
  }

  const metadata = await fetchPromise;
  if (!metadata && cacheKey) {
    // Only the creator of this failed fetch reaches this branch; callers that
    // joined the in-flight promise return from the cache-hit path above.
    annotationMetadataCache.delete(cacheKey);
  }
  return metadata;
}

/**
 * Fetches and parses annotation metadata JSON from a pre-fetched download URL.
 *
 * Use this when you already have the download URL to avoid redundant getDownloadURL() calls.
 * Useful in Firestore functions or when URLs are cached.
 *
 * @param url - Firebase Storage download URL for metadata.json
 * @returns Parsed CaptureMetadata object, or null if unavailable/invalid
 *
 * @example
 * const downloadURL = await getDownloadURL(storageRef);
 * const metadata = await getAnnotationMetadataFromURL(downloadURL);
 */
export async function getAnnotationMetadataFromURL(url: string): Promise<CaptureMetadata | null> {
  if (!url) {
    return null;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`Failed to fetch metadata from URL: ${response.status} ${response.statusText}`);
      return null;
    }

    const metadata: CaptureMetadata = await response.json();
    return metadata;
  } catch (error) {
    if (error instanceof Error) {
      console.warn(`Error parsing metadata from URL:`, error.message);
    }
    return null;
  }
}

export function formatTimestampReadable(timestamp?: Timestamp): string {
  if (!timestamp) return "Unknown time";
  const date = timestamp.toDate();
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function getTranscriptionSamplesForPlot(
  entryCollections: EntryCollection[],
  plotId?: string | null
): TranscriptionSample[] {
  const samples: TranscriptionSample[] = [];

  entryCollections.forEach((collection) => {
    if (collection.type !== "transcription") return;

    const collectionPlotId = typeof collection.plotRef === "string" ? collection.plotRef : collection.plotRef?.id;
    if (plotId && collectionPlotId !== plotId) return;

    const entries = collection.transcriptionEntries || {};
    const createdByRef = collection.createdBy as DocumentReference | undefined;
    const collectionTimestamp = (collection.updatedAt as Timestamp) || (collection.createdAt as Timestamp);

    Object.entries(entries).forEach(([sampleKey, entry]) => {
      const entryTimestamp = (entry.updatedAt as Timestamp) || (entry.createdAt as Timestamp) || collectionTimestamp;

      samples.push({
        plotId: collectionPlotId || "",
        sampleKey,
        text: entry.text || "",
        cloudAudioPath: entry.cloudAudioPath,
        timestamp: entryTimestamp,
        createdByRef,
        createdById: createdByRef?.id,
      });
    });
  });

  samples.sort((a, b) => {
    const aTs = a.timestamp?.seconds || 0;
    const bTs = b.timestamp?.seconds || 0;
    if (aTs !== bTs) return aTs - bTs;
    return Number(a.sampleKey) - Number(b.sampleKey);
  });

  return samples;
}

/**
 * Represents a single entry with its value and metadata
 */
export interface FormattedEntry {
  sampleKey: string;
  value: any;
  images: EntryImage[];
  location?: {
    latitude: number;
    longitude: number;
    accuracyMeters?: number;
    altitudeMeters?: number;
    verticalAccuracyMeters?: number;
    sourceAccuracy?: string;
    delayAfterDataCaptureSeconds?: number;
  };
  createdAt: any;
  updatedAt: any;
  type: string;
  /** OCR-extracted text from the entry's image */
  extractedImageText?: string;
  /** Whether the user manually edited the extracted text */
  extractedImageTextUserDidEdit?: boolean;
}

/**
 * Extract all images from an entry collection
 * @param collection - The entry collection to extract images from
 * @param traitName - Optional trait name to include in each image for better file naming
 */
export function extractImagesFromEntryCollection(collection: EntryCollection, traitName?: string): EntryImage[] {
  const images: EntryImage[] = [];

  // Photo entries
  if (collection.photoEntries) {
    Object.entries(collection.photoEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Text entries with images
  if (collection.textEntries) {
    Object.entries(collection.textEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Number entries with images
  if (collection.numberEntries) {
    Object.entries(collection.numberEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Category entries with images
  if (collection.categoryEntries) {
    Object.entries(collection.categoryEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Date entries with images
  if (collection.dateEntries) {
    Object.entries(collection.dateEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Boolean entries with images
  if (collection.booleanEntries) {
    Object.entries(collection.booleanEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Percentage entries with images
  if (collection.percentageEntries) {
    Object.entries(collection.percentageEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "photo",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  // Leaf Area Index entries (original and annotated images)
  if (collection.leafAreaIndexEntries) {
    Object.entries(collection.leafAreaIndexEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "leafAreaIndex",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
          analysisStatus: entry.analysisStatus,
          analysisReviewStatus: entry.analysisReviewStatus,
        });
      }
      if (entry.annotatedImageCloudPath) {
        images.push({
          url: entry.annotatedImageCloudPath,
          type: "annotated",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
        });
      }
    });
  }

  // Image Measurement entries (original image only)
  if (collection.imageMeasurementEntries) {
    Object.entries(collection.imageMeasurementEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "imageMeasurement",
          reviewPriority: entry.reviewPriority,
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          // Firestore can store an explicit null here; normalize it away so the
          // viewer reads "consulted and absent" rather than "never plumbed".
          depthCloudPath: entry.depthCloudPath ?? undefined,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
          analysisStatus: entry.analysisStatus,
          analysisReviewStatus: entry.analysisReviewStatus,
        });
      }
    });
  }

  // Stand Count Measurement entries (original image only)
  if (collection.standCountMeasurementEntries) {
    Object.entries(collection.standCountMeasurementEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "standCountMeasurement",
          reviewPriority: entry.reviewPriority,
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          // Stand-count captures support 3D endpoint editing too; normalize the
          // Firestore null so the viewer reads "consulted and absent".
          depthCloudPath: entry.depthCloudPath ?? undefined,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
          analysisStatus: entry.analysisStatus,
          analysisReviewStatus: entry.analysisReviewStatus,
        });
      }
    });
  }

  // Row Planted Stand Count entries (images from standCountMedia map)
  if (collection.rowPlantedStandCountEntries) {
    Object.entries(collection.rowPlantedStandCountEntries).forEach(([key, entry]) => {
      // Extract images from the standCountMedia map
      if (entry.standCountMedia) {
        Object.entries(entry.standCountMedia).forEach(([mediaKey, mediaEntry]) => {
          if (mediaEntry.imgCloudPath) {
            images.push({
              url: mediaEntry.imgCloudPath,
              type: "rowPlantedStandCount",
              dataCollectionOnly: mediaEntry.dataCollectionOnly,
              displaySampleKey: mediaEntry.dataCollectionOnly ? key : undefined,
              sampleKey: `${key}-${mediaKey}`,
              uploadStatus: mediaEntry.uploadStatus,
              captureMetadataCloudPath: mediaEntry.captureMetadataCloudPath,
              // Not editable today, but carry the evidence so a future editable
              // sequence never silently locks on a plumbing gap.
              depthCloudPath: mediaEntry.depthCloudPath ?? undefined,
              collectionId: collection.id,
              traitName,
              updatedAt: mediaEntry.updatedAt,
            });
          }
        });
      }
    });
  }

  // Ruler entries (original image only)
  if (collection.rulerEntries) {
    Object.entries(collection.rulerEntries).forEach(([key, entry]) => {
      if (entry.imgCloudPath) {
        images.push({
          url: entry.imgCloudPath,
          type: "ruler",
          sampleKey: key,
          uploadStatus: entry.uploadStatus,
          captureMetadataCloudPath: entry.captureMetadataCloudPath,
          collectionId: collection.id,
          traitName,
          updatedAt: entry.updatedAt,
          extractedImageText: entry.extractedImageText,
          extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        });
      }
    });
  }

  return images;
}

/**
 * Get total entry count from an entry collection
 */
export function getEntryCount(collection: EntryCollection): number {
  let count = 0;

  if (collection.textEntries) count += Object.keys(collection.textEntries).length;
  if (collection.numberEntries) count += Object.keys(collection.numberEntries).length;
  if (collection.categoryEntries) count += Object.keys(collection.categoryEntries).length;
  if (collection.dateEntries) count += Object.keys(collection.dateEntries).length;
  if (collection.transcriptionEntries) count += Object.keys(collection.transcriptionEntries).length;
  if (collection.photoEntries) count += Object.keys(collection.photoEntries).length;
  if (collection.booleanEntries) count += Object.keys(collection.booleanEntries).length;
  if (collection.percentageEntries) count += Object.keys(collection.percentageEntries).length;
  if (collection.leafAreaIndexEntries) count += Object.keys(collection.leafAreaIndexEntries).length;
  if (collection.rulerEntries) count += Object.keys(collection.rulerEntries).length;
  if (collection.imageMeasurementEntries) count += Object.keys(collection.imageMeasurementEntries).length;
  if (collection.standCountMeasurementEntries) count += Object.keys(collection.standCountMeasurementEntries).length;
  if (collection.rowPlantedStandCountEntries) count += Object.keys(collection.rowPlantedStandCountEntries).length;
  if (collection.timerEntries) count += Object.keys(collection.timerEntries).length;

  return count;
}

/**
 * Format a value for display based on its type
 */
export function formatEntryValue(value: any, type: string): string {
  if (value === null || value === undefined) return "N/A";

  switch (type) {
    case "number":
    case "percentage":
    case "leafAreaIndex":
      return typeof value === "number" ? value.toFixed(2) : String(value);
    case "timer": {
      // value is the final elapsed duration in milliseconds (raw ms). A running
      // sample (no elapsedMs) arrives as null/undefined and was handled above.
      const formatted = typeof value === "number" ? formatTimerDuration(value) : "";
      return formatted || "N/A";
    }
    case "boolean":
      return value ? "Yes" : "No";
    case "date":
      return value?.toDate ? value.toDate().toLocaleDateString() : String(value);
    case "ruler":
      return Array.isArray(value) ? `${value.length} measurement(s)` : String(value);
    case "imageMeasurement":
      return `${value} object(s)`;
    case "standCountMeasurement": {
      if (typeof value !== "number") return `${value} per m²`;
      const perAcre = ` (${formatRoundedCountPerSquareMeterPerAcre(value)}/acre)`;
      return `${formatMetricValue(value, "countPerM2")} plants/m²${perAcre}`;
    }
    case "rowPlantedStandCount":
      // Handle both legacy number value and new object format
      if (typeof value === "object" && value !== null) {
        return typeof value.plantsPerMeter === "number" ? `${value.plantsPerMeter.toFixed(1)} plants/m` : "N/A";
      }
      return `${value} plants/m`;
    default:
      return String(value);
  }
}

/**
 * Get all entries from a collection as formatted entries
 * @param collection - The entry collection to format
 * @param traitName - Optional trait name to include in each image for better file naming
 */
export function getFormattedEntries(collection: EntryCollection, traitName?: string): FormattedEntry[] {
  const entries: FormattedEntry[] = [];

  // Helper to create formatted entry
  const createEntry = (
    collectionId: string,
    sampleKey: string,
    entry: any,
    value: any,
    type: string
  ): FormattedEntry => {
    const images: EntryImage[] = [];

    // Extract images based on entry type
    if (entry.imgCloudPath) {
      const imageType =
        type === "leafAreaIndex"
          ? "leafAreaIndex"
          : type === "imageMeasurement"
            ? "imageMeasurement"
            : type === "standCountMeasurement"
              ? "standCountMeasurement"
              : type === "ruler"
                ? "ruler"
                : "photo";

      images.push({
        url: entry.imgCloudPath,
        type: imageType,
        sampleKey,
        uploadStatus: entry.uploadStatus,
        captureMetadataCloudPath: entry.captureMetadataCloudPath,
        traitName,
        collectionId,
        updatedAt: entry.updatedAt,
        extractedImageText: entry.extractedImageText,
        extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
        analysisStatus: entry.analysisStatus,
        analysisReviewStatus: entry.analysisReviewStatus,
      });
    }

    // Only leafAreaIndex entries have annotated images
    // TODO: Fix this. Eventually, we're not going to save annotated images at all.
    if (type === "leafAreaIndex" && entry.annotatedImageCloudPath) {
      images.push({
        url: entry.annotatedImageCloudPath,
        type: "annotated",
        sampleKey,
        uploadStatus: entry.uploadStatus,
        collectionId,
        traitName,
        updatedAt: entry.updatedAt,
      });
    }

    // rowPlantedStandCount entries have images in standCountMedia map
    if (type === "rowPlantedStandCount" && entry.standCountMedia) {
      Object.entries(entry.standCountMedia).forEach(([mediaKey, mediaEntry]: [string, any]) => {
        if (mediaEntry.imgCloudPath) {
          images.push({
            url: mediaEntry.imgCloudPath,
            type: "rowPlantedStandCount",
            sampleKey: `${sampleKey}-${mediaKey}`,
            dataCollectionOnly: mediaEntry.dataCollectionOnly,
            displaySampleKey: mediaEntry.dataCollectionOnly === true ? sampleKey : undefined,
            uploadStatus: mediaEntry.uploadStatus,
            collectionId,
            traitName,
            updatedAt: mediaEntry.updatedAt,
            captureMetadataCloudPath: mediaEntry.captureMetadataCloudPath,
          });
        }
      });
    }

    return {
      sampleKey,
      value,
      images,
      location: entry.location
        ? {
            latitude: entry.location.coordinates.latitude,
            longitude: entry.location.coordinates.longitude,
            accuracyMeters: entry.location.accuracyMeters,
            altitudeMeters: entry.location.altitudeMeters,
            verticalAccuracyMeters: entry.location.verticalAccuracyMeters,
            sourceAccuracy: entry.location.sourceAccuracy,
            delayAfterDataCaptureSeconds: entry.location.delayAfterDataCaptureSeconds,
          }
        : undefined,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
      type,
      extractedImageText: entry.extractedImageText,
      extractedImageTextUserDidEdit: entry.extractedImageTextUserDidEdit,
    };
  };

  // Extract entries based on type
  if (collection.textEntries) {
    Object.entries(collection.textEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.text, "text"));
    });
  }

  if (collection.numberEntries) {
    Object.entries(collection.numberEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.val, "number"));
    });
  }

  if (collection.categoryEntries) {
    Object.entries(collection.categoryEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.selections, "category"));
    });
  }

  if (collection.dateEntries) {
    Object.entries(collection.dateEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.date, "date"));
    });
  }

  if (collection.booleanEntries) {
    Object.entries(collection.booleanEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.val, "boolean"));
    });
  }

  if (collection.percentageEntries) {
    Object.entries(collection.percentageEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.val, "percentage"));
    });
  }

  if (collection.leafAreaIndexEntries) {
    Object.entries(collection.leafAreaIndexEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.val, "leafAreaIndex"));
    });
  }

  if (collection.rulerEntries) {
    Object.entries(collection.rulerEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.measurementsMeters, "ruler"));
    });
  }

  if (collection.imageMeasurementEntries) {
    Object.entries(collection.imageMeasurementEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.objectCount, "imageMeasurement"));
    });
  }

  if (collection.standCountMeasurementEntries) {
    Object.entries(collection.standCountMeasurementEntries).forEach(([key, entry]) => {
      // NOTE: Currently showing countPerSquareMeter as the default value
      // In the future, this could be made metric-aware to show different fields
      // (rawCount, avgLength, avgWidth, totalWeight) based on user selection
      entries.push(createEntry(collection.id, key, entry, entry.countPerSquareMeter, "standCountMeasurement"));
    });
  }

  if (collection.rowPlantedStandCountEntries) {
    Object.entries(collection.rowPlantedStandCountEntries).forEach(([key, entry]) => {
      // Pass all metrics for rowPlantedStandCount entries
      const value = {
        plantsPerMeter: entry.plantsPerMeter,
        averageSpacingMeters: entry.averageSpacingMeters,
        averageDiameterMeters: entry.averageDiameterMeters,
        averageWidthMeters: entry.averageWidthMeters,
        averageVari: entry.averageVari,
        plantCount: entry.plantCount,
        walkDistanceMeters: entry.walkDistanceMeters,
        numPlantLines: entry.numPlantLines,
      };
      entries.push(createEntry(collection.id, key, entry, value, "rowPlantedStandCount"));
    });
  }

  if (collection.timerEntries) {
    Object.entries(collection.timerEntries).forEach(([key, entry]) => {
      // Final stopwatch duration in ms. Idle (elapsedMs absent/0) and running
      // (startedAtEpochMs set, elapsedMs absent) samples yield null so they
      // render as blank/in-progress.
      entries.push(createEntry(collection.id, key, entry, getStoppedTimerMs(entry), "timer"));
    });
  }

  if (collection.photoEntries) {
    Object.entries(collection.photoEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, "Photo", "photo"));
    });
  }

  if (collection.transcriptionEntries) {
    Object.entries(collection.transcriptionEntries).forEach(([key, entry]) => {
      entries.push(createEntry(collection.id, key, entry, entry.text, "transcription"));
    });
  }

  return entries;
}

/**
 * Get the latest value from an entry collection
 */
export function getLatestEntryValue(collection: EntryCollection): any {
  const entries = getFormattedEntries(collection);
  if (entries.length === 0) return null;

  // Sort by createdAt and get the latest
  const sorted = entries.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0;
    const bTime = b.createdAt?.seconds || 0;
    return bTime - aTime;
  });

  return sorted[0]?.value;
}
